import { formatTimecode, type TimecodeSpec } from "@infoflyt/shared/timecode";

import { ProduksjonVelger } from "./ProduksjonVelger";
import { useSession } from "../state/SessionProvider";
import { Merke } from "../components/Merke";
import type { Tidskodetilstand } from "../components/jamTekst";

const p2 = (n: number): string => String(n).padStart(2, "0");

export interface TopBarProps {
  /** Tidskoden som skal stå. Null når siden ikke viser tidskode. */
  readonly frames: number | null;
  /**
   * Om tidskoden er SATT for produksjonen.
   *
   * `ujammet` gir streker: ingen har jammet, og appens egen klokke er
   * ikke en tidskode noen har avtalt. `gammel` gir rødt — siste jam er
   * eldre enn et døgn. NULL betyr at tidskoden er skrudd av i menyen, og
   * da står veggklokka. Se components/jamTekst.ts.
   */
  readonly tcTilstand: Tidskodetilstand | null;
  readonly spec: TimecodeSpec;
  /** Sant når tallet er nåtid, usant når man blar tilbake eller skriver. */
  readonly live: boolean;
  readonly wallUtcMs: number;
  readonly uncertainSince: number | null;
  /**
   * Produksjonen regi logger paa. «Privat» naar det er utenfor en produksjon.
   *
   * ALDRI null. Det er den eneste teksten under tidskoden naar ingenting er
   * galt, og privat maa vaere like synlig som en produksjon — ellers er det
   * den tilstanden man ikke ser man er i.
   */
  readonly productionName: string;
  /** Alle kjente produksjoner, med «privat». Til nedtrekket under tidskoden. */
  readonly produksjoner: readonly { readonly id: string; readonly navn: string }[];
  readonly aktivProduksjonId: string;
  onVelgProduksjon(id: string): void;
  readonly scrolledBack: boolean;
  readonly editMode: boolean;
  readonly menuOpen: boolean;
  onMenu(): void;
  onTimecode(): void;
}

/**
 * Toppstripa. Tre elementer: merket, avlesningen, menyen.
 *
 * Tidskoden står på alle flatene og er det eneste fargede elementet.
 * Veggklokka står ikke ved siden av — den trer inn bare når tidskoden er
 * skrudd av i menyen.
 *
 * Begge deler er endret fra docs/12-ui-feltapp.md og docs/13-design.md, som
 * sier at veggklokka alltid står og at tidskoden bare hører hjemme på de to
 * loggsidene.
 *
 * Avlesningen bytter farge fra rav til grå mens man blar tilbake, fordi
 * markører alltid settes på nåtid. Uten et visuelt skille kan man trykke en
 * hurtigknapp i den tro at man står et annet sted. Se docs/13-design.md.
 */
export function TopBar({
  frames, spec, live, wallUtcMs, uncertainSince, tcTilstand,
  productionName, produksjoner, aktivProduksjonId, onVelgProduksjon,
  scrolledBack, editMode, menuOpen, onMenu, onTimecode,
}: TopBarProps): JSX.Element {
  /* Leses her og ikke sendt inn: `App.tsx` har ingen annen bruk for den,
     og en prop til gjennom toppstripa for ett svar er stoey. */
  const { kanJamme } = useSession();
  const wall = new Date(wallUtcMs);
  const clock = `${p2(wall.getHours())}:${p2(wall.getMinutes())}`;

  const tc = frames === null ? null : formatTimecode(frames, spec);

  /*
   * STREKER NAAR INGEN HAR JAMMET.
   *
   * Samme form som paa nettflata, og med vilje: de to flatene skal si
   * det samme om den samme produksjonen. Trykkflata beholdes for dem som
   * KAN jamme — det er nettopp her teknikeren setter den.
   */
  const ujammet = tcTilstand === "ujammet";
  const vist: string | null = ujammet ? "--:--:--:--" : tc;
  const tilstandsklasse = ujammet || tcTilstand === "gammel"
    ? " readout--ujammet" : "";

  /*
   * Linja under tidskoden sier HVOR du logger, ikke naar tidskoden ble satt.
   *
   * «Satt 06:12, 9 t siden» sto her foer og er flyttet til menyen, under
   * produksjonen. Grunnen er at det er lett aa begynne aa logge paa PRIVAT
   * naar man bytter mellom produksjoner regelmessig, og det er en feil som
   * ikke oppdages foer noen leter etter postene et annet sted. Da maa den
   * ene linja som alltid staar svare paa det spoersmaalet, og ikke paa et
   * som sjelden stilles.
   *
   * PRIVAT VISES OGSAA, og det er hele poenget. Sto privat med blank linje,
   * ville den farlige tilstanden vaert den usynlige.
   *
   * Advarselen tar linja alene. «Tellingen ble brutt 00:00. Tidskoden er
   * usikker.» fyller bredden paa en telefon i seg selv, og den er sjelden
   * naa — bare naar veggklokka faktisk ikke holder. Se assessWallClock.
   */
  const advarsel = uncertainSince !== null
    ? `Tellingen ble brutt ${p2(new Date(uncertainSince).getHours())}:`
      + `${p2(new Date(uncertainSince).getMinutes())}. Tidskoden er usikker.`
    : null;

  // Kort nok til aa staa ved siden av navnet, og verdt plassen: tidskoden
  // som vises er ikke naatid, saa en markoer havner et annet sted enn den ser.
  const tillegg = scrolledBack ? "Blar tilbake" : null;

  return (
    <>
      <div className="topbar">
        <div className="topbar__side">
          <Merke />
        </div>

        <div className="topbar__centre">
          {vist === null ? (
            <span className="mono wallclock" style={{ fontSize: 25, lineHeight: 1 }}>
              {clock}
            </span>
          ) : (
            <>
              {/* Veggklokka står ikke under tidskoden. Den vises bare når
                  tidskoden er skrudd av, slik at toppstripa ikke blir tom. */}
              {/*
                * AVLESNINGEN ER JAM-KNAPPEN, og den finnes bare for dem som
                * skal jamme — tekniker, VJ og produsent, samt enhver paa en
                * privat logg. Uten retten staar tidskoden der som en ren
                * avlesning: den skal SES av alle, bare ikke endres av alle.
                * En knapp som alltid feiler er verre enn ingen knapp.
                * Avgjort 27.10.2026, se state/SessionProvider.tsx.
                */}
              {kanJamme ? (
                <button
                  type="button"
                  className={`mono readout ${live ? "readout--now" : "readout--past"}`
                    + tilstandsklasse}
                  onClick={onTimecode}
                  aria-label={ujammet
                    ? "Tidskoden er ikke satt. Trykk for å jamme."
                    : `Tidskode ${vist}. Trykk for å justere.`}
                >
                  {vist.slice(0, 8)}
                  <span className="readout__ff">:{vist.slice(9)}</span>
                </button>
              ) : (
                <span
                  className={`mono readout ${live ? "readout--now" : "readout--past"}`
                    + tilstandsklasse}
                  aria-label={ujammet ? "Tidskoden er ikke satt" : `Tidskode ${vist}`}
                >
                  {vist.slice(0, 8)}
                  <span className="readout__ff">:{vist.slice(9)}</span>
                </span>
              )}
            </>
          )}
        </div>

        <button
          type="button"
          className="topbar__side"
          onClick={onMenu}
          aria-label={editMode ? "Ferdig" : menuOpen ? "Lukk menyen" : "Meny"}
          aria-expanded={editMode ? undefined : menuOpen}
        >
          {editMode ? (
            <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>Ferdig</span>
          ) : (
            <span
              className={`menu-icon${menuOpen ? " menu-icon--open" : ""}`}
              aria-hidden="true"
            >
              <span /><span /><span />
            </span>
          )}
        </button>
      </div>

      {/* Satt-linja står direkte på svart, mellom flatene. */}
      {/* Linja under tidskoden. `position: relative` fordi
          produksjonsskuffen henger fra underkanten av den. */}
      <div className="undertc">
        {advarsel !== null ? (
          <span className="mono undertc__linje undertc__linje--bad">{advarsel}</span>
        ) : (
          /*
           * Linja er ogsaa veien til aa bytte produksjon.
           *
           * Det er der spoersmaalet oppstaar: ser du «Privat» naar du skulle
           * skrevet paa produksjonen, skal rettingen vaere ett trykk unna.
           * Samme handling finnes i menyen — se ProduksjonVelger.
           */
          <ProduksjonVelger
            navn={productionName}
            tillegg={tillegg}
            produksjoner={produksjoner}
            aktivId={aktivProduksjonId}
            onVelg={onVelgProduksjon}
          />
        )}
      </div>
    </>
  );
}
